import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, AppBar, Toolbar, Typography, Button, Container } from '@mui/material';
import AdminDashboard from './components/AdminDashboard';
import SupplierDashboard from './components/SupplierDashboard';
import UnregisteredUser from './components/UnregisteredUser';
import UserManagement from './components/UserManagement';
import Loading from './components/Loading';
import Login from './components/Login';
import TodoComponent from './components/TodoComponent';
import TodoAdminComponent from './components/TodoAdminComponent';
import { UserProvider } from './contexts/UserContext';
import api from './services/api';

function AdminNavigation() {
  const { logout } = useAuth0();
  
  const handleLogout = () => {
    // Clear token cache on logout
    localStorage.removeItem('auth_token');
    logout({ returnTo: window.location.origin });
  };
  
  return (
    <AppBar 
      position="static"
      sx={{ 
        boxShadow: '0 1px 8px rgba(0,0,0,0.1)',
        bgcolor: 'white',
        color: 'text.primary',
        minHeight: 80
      }}
    >
      <Toolbar sx={{ minHeight: 80 }}>
        <img src="logo192.png" alt="Logo" style={{ height: 56, marginRight: 32, marginLeft: 8 }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Aderbid Cloud
        </Typography>
        <Button color="inherit" href="/">Dashboard</Button>
        <Button color="inherit" href="/todos">Todos</Button>
        <Button color="inherit" href="/admin/todos">All Todos</Button>
        <Button color="inherit" href="/admin/users">Users</Button>
        <Button 
          color="primary" 
          variant="outlined"
          onClick={handleLogout}
          sx={{ 
            borderRadius: '8px', 
            textTransform: 'none',
            fontWeight: 500,
            ml: 2
          }}
        >
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  );
}

function SupplierHeader() {
  const { logout, user } = useAuth0();
  
  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    logout({ returnTo: window.location.origin });
  };
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'flex-end',
        p: 2,
        borderBottom: '1px solid #f0f0f0',
        minHeight: 80
      }}
    >
      <img src="logo192.png" alt="Logo" style={{ height: 56, marginRight: 'auto', marginLeft: 8 }} />
      <Button 
        color="primary" 
        variant="outlined"
        onClick={handleLogout}
        sx={{ 
          borderRadius: '8px', 
          textTransform: 'none',
          fontWeight: 500
        }}
      >
        Logout
      </Button>
    </Box>
  );
}

function Footer() {
  return (
    <Box 
      component="footer" 
      sx={{ 
        py: 2, 
        textAlign: 'center', 
        borderTop: '1px solid #f0f0f0',
        mt: 'auto'
      }}
    >
      <Typography variant="body2" color="text.secondary">
        All rights reserved to paychex-cfo Ltd
      </Typography>
    </Box>
  );
}

function App() {
  const { isLoading: isAuth0Loading, isAuthenticated, loginWithRedirect, user, getAccessTokenSilently } = useAuth0();
  const [currentUser, setCurrentUser] = React.useState(null);
  const [isLoadingUser, setIsLoadingUser] = React.useState(true);
  const [isUnregistered, setIsUnregistered] = React.useState(false);

  // Memoized function to get token, using cache when possible
  const getToken = React.useCallback(async () => {
    const now = Date.now();
    if (window.tokenCache && window.tokenCache.token && window.tokenCache.expiresAt && now < window.tokenCache.expiresAt) {
      return window.tokenCache.token;
    }
    try {
      const token = await getAccessTokenSilently({ cacheMode: 'cache-only' });
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        if (payload.exp) {
          window.tokenCache = {
            expiresAt: (payload.exp * 1000) - (5 * 60 * 1000),
            token
          };
        }
      }
      return token;
    } catch (error) {
      const token = await getAccessTokenSilently();
      return token;
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    const fetchUser = async () => {
      if (!isAuthenticated || !user?.email) {
        setIsLoadingUser(false);
        return;
      }
      try {
        const token = await getToken();
        localStorage.setItem('auth_token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/me');
        setCurrentUser(response.data);
        if (response.data && response.data.is_registered === false) {
          setIsUnregistered(true);
        } else {
          setIsUnregistered(false);
        }
      } catch (error) {
        if (error.response?.status === 404) {
          setCurrentUser(null);
          setIsUnregistered(true);
        }
      } finally {
        setIsLoadingUser(false);
      }
    };
    fetchUser();
  }, [isAuthenticated, user, getToken]);

  if (isAuth0Loading || isLoadingUser) {
    return <Loading />;
  }
  if (!isAuthenticated) {
    return (
      <>
        <Login onLogin={loginWithRedirect} />
        <Footer />
      </>
    );
  }
  if (isUnregistered && (!currentUser || !currentUser.is_admin)) {
    return (
      <>
        <UnregisteredUser email={user?.email} />
        <Footer />
      </>
    );
  }
  if (!currentUser) {
    return <Loading />;
  }
  return (
    <UserProvider currentUser={currentUser}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {!(currentUser.is_admin || currentUser.is_admin_viewer) && <SupplierHeader />}
        <Container sx={{ flexGrow: 1 }}>
          <Routes>
            <Route 
              path="/" 
              element={
                (currentUser.is_admin || currentUser.is_admin_viewer) ? 
                  <AdminDashboard /> : 
                  <SupplierDashboard />
              } 
            />
            <Route path="/todos" element={<TodoComponent />} />
            {(currentUser.is_admin || currentUser.is_admin_viewer) && (
              <>
                <Route path="/admin/todos" element={<TodoAdminComponent />} />
                <Route path="/admin/users" element={<UserManagement />} />
              </>
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Container>
        <Footer />
      </Box>
    </UserProvider>
  );
}

export default App;
